// ─── Permissions matrix ───────────────────────────────────────────────────────
// Single source of truth for what each role can do.
// Used by usePermission(), PermissionGate, and route guards.

export type { UserRole } from '@/types/maintenance';

export type Permission =
  // Work Orders
  | 'wo:view'
  | 'wo:create'
  | 'wo:update_own'    // assigned to me or created by me
  | 'wo:update_any'    // any WO in the org
  | 'wo:delete'
  // Molds
  | 'molds:view'
  | 'molds:create'
  | 'molds:edit'
  | 'molds:delete'
  // Spare Parts
  | 'parts:view'
  | 'parts:transact'   // receive / issue / adjust stock
  | 'parts:manage'     // create / delete part records
  // Planning / Readiness
  | 'planning:view'
  | 'planning:manage'
  // Reports
  | 'reports:view'
  // Capacity
  | 'capacity:view'
  // Settings
  | 'settings:view'
  | 'settings:manage'  // add / disable codes & templates
  // Users
  | 'users:view'
  | 'users:manage'     // role changes, deactivation
  // Audit
  | 'audit:view'
  // Inject module
  | 'inject:view'
  | 'inject:manage'
  // Quality module
  | 'quality:view'
  | 'quality:create'   // create NCs, log scrap
  | 'quality:manage';  // update NC status, close, manage catalog

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  maintenance_only: [
    'wo:view', 'wo:create', 'wo:update_own',
    'molds:view',
    'parts:view', 'parts:transact',
    'planning:view',
    'reports:view',
    'settings:view',
    // No inject:*, no quality:* — strict maintenance-only (but reports allowed)
  ],

  technician: [
    'wo:view', 'wo:create', 'wo:update_own',
    'molds:view',
    'parts:view', 'parts:transact',
    'planning:view',
    'settings:view',
    'quality:view', 'quality:create',
  ],

  manager: [
    'wo:view', 'wo:create', 'wo:update_own', 'wo:update_any',
    'molds:view', 'molds:create', 'molds:edit',
    'parts:view', 'parts:transact', 'parts:manage',
    'planning:view', 'planning:manage',
    'reports:view',
    'capacity:view',
    'settings:view', 'settings:manage',
    'users:view',
    'inject:view', 'inject:manage',
    'quality:view', 'quality:create', 'quality:manage',
  ],

  admin: [
    'wo:view', 'wo:create', 'wo:update_own', 'wo:update_any', 'wo:delete',
    'molds:view', 'molds:create', 'molds:edit', 'molds:delete',
    'parts:view', 'parts:transact', 'parts:manage',
    'planning:view', 'planning:manage',
    'reports:view',
    'capacity:view',
    'settings:view', 'settings:manage',
    'users:view', 'users:manage',
    'audit:view',
    'inject:view', 'inject:manage',
    'quality:view', 'quality:create', 'quality:manage',
  ],

  super_admin: [
    'wo:view', 'wo:create', 'wo:update_own', 'wo:update_any', 'wo:delete',
    'molds:view', 'molds:create', 'molds:edit', 'molds:delete',
    'parts:view', 'parts:transact', 'parts:manage',
    'planning:view', 'planning:manage',
    'reports:view',
    'capacity:view',
    'settings:view', 'settings:manage',
    'users:view', 'users:manage',
    'audit:view',
    'inject:view', 'inject:manage',
    'quality:view', 'quality:create', 'quality:manage',
  ],
};

export function hasPermission(
  role: string | undefined | null,
  permission: Permission,
): boolean {
  if (!role) return false;
  return (ROLE_PERMISSIONS[role as UserRole] ?? []).includes(permission);
}
